import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';

import withI18n from '../components/withI18n';
import HomeSearchContainer from '../containers/HomeSearchContainer';
import OrderRating from '../containers/OrderRating';
import AppBannerContainer from '../containers/AppBannerContainer';
import HomeLayout from '../components/HomeLayout';
import HomeMapLayout from '../containers/HomeMapLayout';
import PageFooter from '../containers/PageFooter';
import AnalyticsTrackers from '../containers/AnalyticsTrackers';
import CreditRedemptionStatus from '../containers/CreditRedemptionStatus';
import MealCardModal from '../containers/MealCardModal';
import HomeFeedLogisticsModal from '../containers/HomeFeedLogisticsModal';
import HomeDateTimePicker from '../containers/HomeDateTimePicker';
import MealCardDialog from '../containers/MealCardDialog';
import Notification from '../containers/Notification';
import ExploreCollectionModalContainer from '../containers/ExploreCollectionModalContainer';
import Page from '../containers/Page';
import { requestBasket } from '../actions/basketActions';
import { getLastOrder } from '../actions/orderActions';
import { requestRecentAddresses } from '../actions/locationActions';

import { requestConfig } from '../actions/configActions';
import { requestInitialHomeFeed, setInitialHomeFilters } from '../actions/homeFeedActions';
import { trackTimeToInteractive } from '../actions/tracking/timeToInteractive';

import {
  getSearchId,
  getNeighborhood,
  showDateTimeModal as getShowDateTimeModal,
  showLogisticsModal as getShowLogisticsModal,
  getIsMobilePlatform,
} from '../selectors/homeSelectors';

import { getIsHomeMapOpen } from '../selectors/homeMapSelectors';

import {
  statusForState,
  buildCanonicalUrl,
  hasQueryStringParams,
  getUrlWithoutLocale,
  getAlternateLocales,
} from '../utils/pages/restaurants';

import Header from '../containers/Header';

import isHomeSearchActive from '../utils/home/isHomeSearchActive';

import i18n from '../utils/i18n';

import sentry from '../utils/sentry';
import CustomErrorPage from './_error';

const { Sentry, captureException } = sentry();
const LocationSearchContainer = dynamic(import('../containers/LocationSearchContainer'), {
  ssr: false,
});

class Restaurants extends Component {
  static async getPageData(context, dispatch, getState) {
    const { req, res, query } = context;
    const isServer = !!req;

    dispatch(setInitialHomeFilters(query));

    await Promise.all([dispatch(requestConfig())]);

    await Promise.all([
      dispatch(requestInitialHomeFeed()),
      dispatch(requestBasket()),
      dispatch(getLastOrder()),
      dispatch(requestRecentAddresses()),
    ]);

    const state = getState();
    const { status, error } = statusForState(state);

    if (res && status) {
      res.statusCode = status;
      if (error) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            'home.city': state.home.filters.location.city,
            'home.neighborhood': state.home.filters.location.neighborhood,
            'home.trackingId': getSearchId(state),
          });
          captureException(error);
        });
      }
    }

    if (isServer && !error) {
      const canonicalUrl = buildCanonicalUrl(req.url, state);
      if (req.url !== canonicalUrl) {
        res.redirect(canonicalUrl);
      }
    }
  }

  render() {
    const {
      urlWithoutLocale,
      neighborhood,
      robots,
      alternateLocales,
      isSearchEnabled,
      showDateTimeModal,
      showLogisticsModal,
      statusCode,
      isConfigError,
      isMobilePlatform,
      isHomeMapOpen,
    } = this.props;

    const pageTitle =
      neighborhood &&
      i18n.gettext('Takeaway delivery in %(neighborhood)s - Order with Deliveroo', {
        neighborhood,
      });

    const pageDescription =
      neighborhood &&
      i18n.gettext('restaurant_list.page.description(%(neighborhood)s)', { neighborhood });

    if (statusCode !== 200 && isConfigError) {
      return <CustomErrorPage statusCode={statusCode} />;
    }
    const platform = isMobilePlatform ? 'mobile' : 'desktop';
    const customTrackingTags = [`web_platform:${platform}`];

    return (
      <Page
        pageName="restaurants"
        trackTTI={(tti) => this.props.trackTimeToInteractive(tti, 'Home')}
        customTrackingTags={customTrackingTags}
        pagePath={urlWithoutLocale}
        pageTitle={pageTitle}
        metaDescription={pageDescription}
        robots={robots}
        alternateLocales={alternateLocales}
        screenHeight={isHomeMapOpen}
      >
        <AppBannerContainer page="restaurants" />
        <Header
          maxWidth="xx-large"
          searchComponent={isSearchEnabled ? <HomeSearchContainer /> : null}
        />
        <CreditRedemptionStatus />
        <MealCardModal />
        <MealCardDialog />
        <OrderRating />
        {isHomeMapOpen ? <HomeMapLayout /> : <HomeLayout />}
        {showLogisticsModal && <HomeFeedLogisticsModal />}
        {showDateTimeModal && <HomeDateTimePicker />}
        <LocationSearchContainer searchType="restaurants" trackingPage="Restaurant List" isModal />
        {!isHomeMapOpen && <PageFooter />}
        <AnalyticsTrackers />

        <Notification />
        <ExploreCollectionModalContainer />
      </Page>
    );
  }
}

Restaurants.propTypes = {
  statusCode: PropTypes.number.isRequired,
  neighborhood: PropTypes.string,
  isConfigError: PropTypes.bool,
  trackTimeToInteractive: PropTypes.func.isRequired,
  urlWithoutLocale: PropTypes.string.isRequired,
  robots: PropTypes.string,
  isSearchEnabled: PropTypes.bool,
  showDateTimeModal: PropTypes.bool.isRequired,
  showLogisticsModal: PropTypes.bool.isRequired,
  alternateLocales: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      hrefLang: PropTypes.string.isRequired,
    }).isRequired,
  ),
  isMobilePlatform: PropTypes.bool,
  isHomeMapOpen: PropTypes.bool.isRequired,
};

Restaurants.defaultProps = {
  neighborhood: undefined,
  robots: undefined,
  alternateLocales: undefined,
  isSearchEnabled: false,
  isConfigError: false,
  isMobilePlatform: undefined,
};

function mapStateToProps(state) {
  const isSearchEnabled = isHomeSearchActive(state);

  const { status, isConfigError } = statusForState(state);

  const neighborhood = getNeighborhood(state);
  const alternateLocales = getAlternateLocales(state.request, state.config);
  const urlWithoutLocale = getUrlWithoutLocale(state.request);

  return {
    statusCode: status,
    isConfigError,
    neighborhood,
    robots: hasQueryStringParams(state.request.currentQuery) && 'noindex, follow',
    alternateLocales,
    urlWithoutLocale,
    isSearchEnabled,
    showDateTimeModal: getShowDateTimeModal(state),
    showLogisticsModal: getShowLogisticsModal(state),
    isMobilePlatform: getIsMobilePlatform(state),
    isHomeMapOpen: getIsHomeMapOpen(state),
  };
}

const mapDispatchToProps = (dispatch) => ({
  trackTimeToInteractive: (tti, pageName) => dispatch(trackTimeToInteractive(tti, pageName)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withI18n(Restaurants));
